// <bundle-builder> — cascading bundle configurator.
// Reads a hidden nested [data-bb-tree] of .bb-node elements (authored as nested
// `bundle-builder-option` theme blocks), renders dependent selector rows, resolves the
// selected leaf (a bundle product), and swaps gallery / price / description / rating /
// "what's inside" + the hidden add-to-cart variant id. Add-to-cart itself runs through
// the theme's own <product-form-component>.

const STAR =
  '<svg class="bundle-builder__star{empty}" viewBox="0 0 20 20" aria-hidden="true">' +
  '<path d="M10 1.5l2.47 5.01 5.53.8-4 3.9.94 5.49L10 14.6l-4.95 2.6.94-5.49-4-3.9 5.53-.8z"/></svg>';

const PLACEHOLDER_IMG =
  '<svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">' +
  '<rect width="100" height="100" fill="rgba(0,0,0,0.04)"/><g fill="rgba(0,0,0,0.10)">' + // audit-ok: neutral svg placeholder, not a theme color
  '<circle cx="34" cy="38" r="11"/><path d="M10 82l26-30 17 19 13-14 24 25z"/></g></svg>';

class BundleBuilder extends HTMLElement {
  connectedCallback() {
    if (this._init) {
      this._bindSticky(); // re-arm the scroll watcher after an editor drag-move (unbound below)
      this._bindVideoWatchers(); // same: IO + document listeners are dropped on disconnect
      this._syncVideo();
      return;
    }
    this._init = true;

    this.cfg = this._readJSON('[data-bb-config]') || {};
    this.sectionId = this.dataset.sectionId || 'bb';
    // unique per load so the browser can't restore stale radio state (which fired spurious change events)
    this._uid = 'u' + Math.random().toString(36).slice(2, 9);
    this._upsellPool = this._parseUpsellPool();
    // pending upsell quantities: key -> qty. Deliberately NOT in the cart until the shopper commits
    // with the main button, and deliberately not persisted (a stale selection restored after a
    // completed checkout is worse than losing it).
    this._upQty = new Map();
    this.tree = this._parseTree(this.querySelector('[data-bb-tree]'));

    this.refs = {
      rows: this.querySelector('[data-bb-rows]'),
      main: this.querySelector('[data-bb-main]'),
      thumbs: this.querySelector('[data-bb-thumbs]'),
      prev: this.querySelector('[data-bb-prev]'),
      next: this.querySelector('[data-bb-next]'),
      price: this.querySelector('[data-bb-price]'),
      compare: this.querySelector('[data-bb-compare]'),
      save: this.querySelector('[data-bb-save]'),
      buyPrice: this.querySelector('[data-bb-buy-price]'),
      lineUnit: this.querySelector('[data-bb-unit]'),
      lineThreshold: this.querySelector('[data-bb-threshold]'),
      lineCustom: this.querySelector('[data-bb-custom-line]'),
      sticky: this.querySelector('[data-bb-sticky]'),
      stickyLabel: this.querySelector('[data-bb-sticky-label]'),
      stickyPrice: this.querySelector('[data-bb-sticky-price]'),
      stickyBtn: this.querySelector('[data-bb-sticky-add]'),
      desc: this.querySelector('[data-bb-desc]'),
      rating: this.querySelector('[data-bb-rating]'),
      stars: this.querySelector('[data-bb-stars]'),
      ratingValue: this.querySelector('[data-bb-rating-value]'),
      ratingCount: this.querySelector('[data-bb-rating-count]'),
      swatches: this.querySelector('[data-bb-swatches]'),
      variantId: this.querySelector('[data-bb-id]'),
      addBtn: this.querySelector('[data-bb-add]'),
      addText: this.querySelector('[data-bb-add-text]'),
      form: this.querySelector('product-form-component'),
      addContainer: this.querySelector('add-to-cart-component'),
      upsell: this.querySelector('[data-bb-upsell]'),
      upsellList: this.querySelector('[data-bb-upsell-list]'),
      // custom bundle (build-your-own) picker
      custom: this.querySelector('[data-bb-custom]'),
      customNum: this.querySelector('[data-bb-custom-num]'),
      customTitle: this.querySelector('[data-bb-custom-title]'),
      customTabs: this.querySelector('[data-bb-custom-tabs]'),
      customColors: this.querySelector('[data-bb-custom-colors]'),
      customHint: this.querySelector('[data-bb-custom-hint]'),
      customCircles: this.querySelector('[data-bb-custom-circles]'),
      customSelLabel: this.querySelector('[data-bb-custom-sel-label]'),
      customMsg: this.querySelector('[data-bb-custom-msg]'),
      props: this.querySelector('[data-bb-props]'),
      // video cover
      videoLayer: this.querySelector('[data-bb-video]'),
      videoPlay: this.querySelector('[data-bb-vplay]'),
      videoSound: this.querySelector('[data-bb-vsound]'),
    };

    this.gallery = { images: [], index: 0 };
    this._initVideo();
    this._bindGallery();
    this._bindCustomAdd();
    this._bindCartDrawerOpen();
    this._bindSticky();

    if (!this.tree.length) {
      if (this.refs.main && !this.refs.main.querySelector('img, svg')) {
        this.refs.main.innerHTML = PLACEHOLDER_IMG;
      }
      this._renderEmpty();
      return;
    }

    // Shareable deep link: restore the selection from the URL param when present, else defaults.
    // The restored path is applied DIRECTLY to this.selection (no synthetic change events —
    // _renderRows re-asserts the radio state itself, cooperating with _settling/_reassertChecked).
    const restore = this._restoreFromUrl();
    if (restore && restore.selection) {
      this.selection = restore.selection;
      // raw picks (custom-bundle colours) are validated + seeded inside _enterCustom's normal init
      this._pendingPicks = restore.rawPicks != null ? restore.rawPicks.split('.') : null;
    } else {
      const firstNode = this.tree.find((n) => n.isDefault) || this.tree[0];
      this.selection = [firstNode].concat(this._pathBelow(firstNode));
    }
    this._renderRows();
    this._updatePanel();
    this._pendingPicks = null;
    if (restore) this._settleUrlAfterRestore(restore);
  }

  _readJSON(sel) {
    const el = this.querySelector(sel);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  // Walk direct .bb-node children into {key,label,spec,badge,isDefault,leaf,children}
  // The upsell pool lives at SECTION level, so it is invisible to every leaf's JSON (content_for
  // blocks are absent from section.blocks). Collect it from the DOM once, keyed by `key`, with each
  // definition's media gathered from ITS OWN subtree — a descendant-wide query on a parent would
  // harvest another upsell's slides.
  _parseUpsellPool() {
    const pool = new Map();
    const defs = this.querySelectorAll('.bb-upsell-def');
    for (const def of Array.from(defs)) {
      const blob = def.querySelector(':scope > script.bb-upsell');
      if (!blob) continue;
      let data = null;
      try {
        data = JSON.parse(blob.textContent);
      } catch (e) {
        data = null;
      }
      if (!data || !data.variantId || !data.key) continue;
      data.media = Array.from(def.querySelectorAll(':scope > script.bb-upmedia'))
        .map((m) => {
          try {
            return JSON.parse(m.textContent);
          } catch (e) {
            return null;
          }
        })
        .filter(Boolean);
      pool.set(String(data.key), data);
    }
    return pool;
  }

  // Resolve a leaf's declaration into the actual upsells to show. 'all' = the pool in author order,
  // 'selected' = the named keys in the order the merchant typed them, 'none' = nothing.
  _upsellsFor(leaf) {
    if (!this._upsellPool || !this._upsellPool.size || !leaf) return [];
    const mode = leaf.upsellMode || 'all';
    if (mode === 'none') return [];
    if (mode !== 'selected') return Array.from(this._upsellPool.values());
    return String(leaf.upsellKeys || '')
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean)
      .map((k) => this._upsellPool.get(k))
      .filter(Boolean);
  }

  _parseTree(container) {
    if (!container) return [];
    const walk = (el) => {
      const out = [];
      for (const child of Array.from(el.children)) {
        if (!child.classList || !child.classList.contains('bb-node')) continue;
        const leafEl = Array.from(child.children).find(
          (c) => c.tagName === 'SCRIPT' && c.classList.contains('bb-leaf')
        );
        let leaf = null;
        if (leafEl) {
          try {
            leaf = JSON.parse(leafEl.textContent);
          } catch (e) {
            leaf = null;
          }
        }
        const isCustom = child.dataset.custom === 'true';
        // a custom-bundle node carries its colours as sibling <script class="bb-color"> blobs
        if (isCustom && leaf && leaf.custom) {
          const colorEls = child.querySelectorAll('script.bb-color');
          leaf.custom.colors = Array.from(colorEls)
            .map((s) => {
              try {
                return JSON.parse(s.textContent);
              } catch (e) {
                return null;
              }
            })
            .filter(Boolean);
        }
        out.push({
          key: child.dataset.key || '',
          label: child.dataset.label || '',
          spec: child.dataset.spec || '',
          badge: child.dataset.badge || '',
          badgeSelected: child.dataset.badgeSelected || '',
          isDefault: child.dataset.default === 'true',
          isCustom,
          rowHeading: child.dataset.rowHeading || '', // heading for THIS node's child row
          leaf,
          children: walk(child),
        });
      }
      return out;
    };
    return walk(container);
  }

  get leaf() {
    const last = this.selection[this.selection.length - 1];
    return last && !last.children.length ? last : null;
  }

  _renderRows() {
    const rows = this.refs.rows;
    if (!rows) return;
    this._gen = (this._gen || 0) + 1; // fresh radio-group names each render
    const gen = this._gen;
    this._settling = true; // suppress browser-fired (non-user) change events right after a render
    rows.innerHTML = '';
    const headings = this.cfg.headings || [];

    // heading for each row comes from the selected PARENT node (its rowHeading), so different
    // branches can label their child rows differently; falls back to the section's per-depth list.
    let sibs = this.tree;
    let level = 0;
    let parent = null;
    while (sibs && sibs.length) {
      const selected = this.selection[level];
      const heading = (parent && parent.rowHeading) || headings[level] || '';
      rows.appendChild(this._buildRow(level, sibs, selected, heading));
      if (!selected || !selected.children.length) break;
      parent = selected;
      sibs = selected.children;
      level++;
    }

    // after the browser has had a chance to auto-restore radios, re-assert our real selection
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        if (gen !== this._gen) return;
        this._reassertChecked();
        this._settling = false;
      })
    );
  }

  // force each row's checked radio to match this.selection (undo any browser auto-restore)
  _reassertChecked() {
    if (!this.refs.rows) return;
    this.refs.rows.querySelectorAll('.bundle-builder__group').forEach((g, level) => {
      const sel = this.selection[level];
      if (!sel) return;
      g.querySelectorAll('input[type="radio"]').forEach((inp) => {
        const want = inp.value === sel.key;
        if (inp.checked !== want) inp.checked = want;
      });
    });
  }

  _buildRow(level, siblings, selected, heading) {
    const fs = document.createElement('fieldset');
    fs.className = 'bundle-builder__group';

    const legend = document.createElement('legend');
    legend.className = 'bundle-builder__group-label';
    legend.innerHTML =
      '<span class="bundle-builder__group-num" aria-hidden="true"></span>' +
      '<span class="bundle-builder__group-name"></span>';
    legend.querySelector('.bundle-builder__group-name').textContent = heading;

    // optional helper link on the first row
    if (level === 0 && this.cfg.rowLink && this.cfg.rowLink.label) {
      const a = document.createElement('a');
      a.className = 'bundle-builder__group-link';
      a.href = this.cfg.rowLink.url || '#';
      a.textContent = this.cfg.rowLink.label;
      legend.appendChild(a);
    }
    fs.appendChild(legend);

    const tiles = document.createElement('div');
    const cols = Math.min(Math.max(siblings.length, 1), 4);
    tiles.className = 'bundle-builder__tiles bundle-builder__tiles--' + cols;

    siblings.forEach((node) => {
      const label = document.createElement('label');
      label.className = 'bundle-builder__tile' + (siblings.length <= 2 ? ' bundle-builder__tile--wide' : '');

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'bb-' + this.sectionId + '-' + this._uid + '-g' + this._gen + '-row-' + level;
      input.value = node.key;
      input.autocomplete = 'off'; // prevent the browser restoring a stale checked state on reload
      if (node === selected) input.checked = true;
      input.addEventListener('change', () => this._onSelect(level, node));
      label.appendChild(input);

      // two badges: one shown when the tile is NOT selected, one when it IS (CSS toggles via :checked)
      if (node.badge) {
        const b = document.createElement('span');
        b.className = 'bundle-builder__tile-badge bundle-builder__tile-badge--unselected';
        b.textContent = node.badge;
        label.appendChild(b);
      }
      if (node.badgeSelected) {
        const bsel = document.createElement('span');
        bsel.className = 'bundle-builder__tile-badge bundle-builder__tile-badge--selected';
        bsel.textContent = node.badgeSelected;
        label.appendChild(bsel);
      }
      const lab = document.createElement('span');
      lab.className = 'bundle-builder__tile-label';
      lab.textContent = node.label;
      label.appendChild(lab);

      if (node.spec) {
        const sp = document.createElement('span');
        sp.className = 'bundle-builder__tile-spec';
        sp.textContent = node.spec;
        label.appendChild(sp);
      }
      tiles.appendChild(label);
    });

    fs.appendChild(tiles);
    return fs;
  }

  _onSelect(level, node) {
    if (this._settling) return; // ignore browser-fired (non-user) changes right after a render
    // keep the path up to this level, set the new node, then re-default below it
    const prefix = this.selection.slice(0, level);
    prefix[level] = node;
    this.selection = prefix.concat(this._pathBelow(node));
    this._renderRows();
    this._updatePanel();
    this._syncUrl(); // reflect the committed selection in the shareable URL (replaceState only)
  }

  // Build the default continuation (levels below `node`) down to a leaf.
  _pathBelow(node) {
    const path = [];
    let sibs = node.children;
    while (sibs && sibs.length) {
      const chosen = sibs.find((n) => n.isDefault) || sibs[0];
      path.push(chosen);
      sibs = chosen.children;
    }
    return path;
  }

  // ---------- shareable selection URL (?bb=key.key.key & ?bb-c=color.color) ----------
  // NOTE: the param is per-PAGE, not per-instance — if this section is placed twice on one
  // page, the LAST-initialized instance wins the initial read, and _syncUrl from either
  // instance overwrites the shared param (acceptable: last interaction wins).

  // all URL read/write is skipped inside the theme editor, and when the merchant turned it off
  _urlEnabled() {
    if (this.cfg.urlEnabled === false) return false;
    return !(window.Shopify && window.Shopify.designMode);
  }

  // sanitized merchant-configured param name; never empty, never a Shopify-meaningful param
  _paramName() {
    const p = String(this.cfg.urlParam || '').replace(/[^A-Za-z0-9_-]/g, '');
    const reserved = ['variant', 'page', 'q', 'sort_by', 'view', 'preview_theme_id'];
    return !p || reserved.indexOf(p.toLowerCase()) !== -1 ? 'bb' : p;
  }

  // Read the deep-link param and resolve it against the tree.
  // Returns null when there is nothing to restore (no param / theme editor);
  // otherwise { selection, rawPath, rawPicks } — selection is null when NO segment
  // matched (fall back to defaults and REMOVE the invalid param from the URL).
  _restoreFromUrl() {
    if (!this._urlEnabled()) return null;
    let raw = null;
    let rawPicks = null;
    try {
      const sp = new URL(location.href).searchParams;
      raw = sp.get(this._paramName());
      rawPicks = sp.get(this._paramName() + '-c');
    } catch (e) {
      return null;
    }
    if (raw == null && rawPicks == null) return null; // plain load with no params — URL untouched
    // orphan picks param with no main param: nothing to restore, but clean the stray -c up
    if (raw == null) return { selection: null, rawPath: raw, rawPicks };
    if (raw === '') return { selection: null, rawPath: raw, rawPicks };
    const segments = raw.split('.');
    let candidates = this.tree;
    const path = [];
    for (const seg of segments) {
      const match = (candidates || []).find((n) => n.key === seg); // first match on duplicate sibling keys
      if (!match) break; // first mismatch: keep the valid prefix, default the rest
      path.push(match);
      candidates = match.children;
    }
    if (!path.length) return { selection: null, rawPath: raw, rawPicks };
    const last = path[path.length - 1];
    return { selection: path.concat(this._pathBelow(last)), rawPath: raw, rawPicks };
  }

  // After a restore: clean the URL ONCE if it didn't match what was actually applied —
  // invalid values are dropped (owner requirement), a corrected path is written back.
  _settleUrlAfterRestore(restore) {
    if (!restore.selection) {
      this._syncUrl(true); // nothing usable in the param — defaults applied, param removed
      return;
    }
    const applied = this.selection.map((n) => n.key).join('.');
    const appliedPicks = this._customActive && this.customState ? this.customState.picks.join('.') : '';
    const pathDirty = applied !== restore.rawPath;
    const picksDirty = restore.rawPicks != null && restore.rawPicks !== appliedPicks;
    if (pathDirty || picksDirty) this._syncUrl();
  }

  // Write the current selection into the URL (history.replaceState — never navigates,
  // never reloads, preserves every other query param, incl. the theme-owned ?variant=).
  _syncUrl(removeAll) {
    if (!this._urlEnabled()) return;
    if (!removeAll && (!this.selection || !this.selection.length)) return;
    try {
      const param = this._paramName();
      const picksParam = param + '-c';
      const url = new URL(location.href);
      if (removeAll) {
        url.searchParams.delete(param);
        url.searchParams.delete(picksParam);
      } else {
        url.searchParams.set(param, this.selection.map((n) => n.key).join('.'));
        const st = this._customActive ? this.customState : null;
        if (st && st.picks.length) url.searchParams.set(picksParam, st.picks.join('.'));
        else url.searchParams.delete(picksParam);
      }
      history.replaceState({}, '', url);
    } catch (e) {}
  }

  _updatePanel() {
    const leaf = this.leaf;
    const data = leaf && leaf.leaf ? leaf.leaf : null;
    this._leafData = data; // ⚠️ `this.leaf` is the NODE — its JSON is node.leaf, not node.variantId
    const isCustom = !!(data && data.custom);
    const available = !!(data && data.available && data.variantId);

    // gallery (the "box" product images in custom mode)
    this._setGallery(
      data && data.images && data.images.length ? data.images : [],
      data && data.video && data.video.sources && data.video.sources.length ? data.video : null
    );

    // price + save (the box / pack price in custom mode)
    if (this.refs.price) this.refs.price.textContent = (data && data.price) || this.cfg.fallbackPrice || '';
    if (this.refs.buyPrice) {
      // custom buy-bar template (precomposed server-side per leaf) wins over the plain price
      const bbl = data && data.buyBarLine;
      if (bbl) {
        this.refs.buyPrice.innerHTML = bbl;
        this.refs.buyPrice.classList.add('bundle-builder__buy-price--custom');
      } else {
        this.refs.buyPrice.textContent = (data && data.price) || this.cfg.fallbackPrice || '';
        this.refs.buyPrice.classList.remove('bundle-builder__buy-price--custom');
      }
    }
    if (this.refs.compare) {
      const c = data && data.compareAt;
      this.refs.compare.textContent = c || '';
      this.refs.compare.hidden = !c;
    }
    if (this.refs.save) {
      // spl engine chip: when the leaf carries the saveChip KEY (new-world leaves always do),
      // it is authoritative — null means save_style 'off' and must NOT fall back to the legacy
      // percent paint. The legacy path only serves stale leaves without the key.
      const hasChipKey = !!data && 'saveChip' in data;
      const chip = data && data.saveChip;
      const pct = data && data.save;
      if (hasChipKey) {
        this.refs.save.innerHTML = chip || '';
        this.refs.save.hidden = !chip;
      } else if (pct) {
        this.refs.save.textContent = (this.cfg.savePrefix || 'חסכי ') + pct + '%';
        this.refs.save.hidden = false;
      } else {
        this.refs.save.hidden = true;
      }
    }

    // sticky buy bar mirrors the buy-bar price + the selection path; its button state follows
    // the real add-to-cart (set later in this method / by the availability logic)
    if (this.refs.stickyPrice) {
      const sbl = data && data.buyBarLine;
      if (sbl) this.refs.stickyPrice.innerHTML = sbl;
      else this.refs.stickyPrice.textContent = (data && data.price) || this.cfg.fallbackPrice || '';
    }
    if (this.refs.stickyLabel) {
      const path = (this.selection || []).map((n) => n.label).filter(Boolean).join(' · ');
      this.refs.stickyLabel.textContent = path;
      this.refs.stickyLabel.hidden = !path;
    }

    // spl price lines (strings precomposed server-side; the auto-threshold line is RESERVED —
    // never hidden, text-swap only, so leaf switches cannot shift the layout)
    if (this.refs.lineUnit) {
      const u = data && data.unitLine;
      this.refs.lineUnit.innerHTML = u || '';
      this.refs.lineUnit.hidden = !u;
    }
    if (this.refs.lineThreshold) {
      const t = data && data.thresholdLine;
      this.refs.lineThreshold.innerHTML = t || '';
      this.refs.lineThreshold.classList.toggle('spl-line--th-ok', !!(data && data.thresholdOver === true));
      this.refs.lineThreshold.classList.toggle('spl-line--th-below', !!(data && data.thresholdOver === false));
    }
    if (this.refs.lineCustom) {
      const cl = data && data.customLine;
      this.refs.lineCustom.innerHTML = cl || '';
      this.refs.lineCustom.hidden = !cl;
    }

    // description
    if (this.refs.desc) {
      const d = (data && data.description) || this.cfg.fallbackDesc || '';
      this.refs.desc.textContent = d;
      this.refs.desc.hidden = !d;
    }

    // rating (leaf metafield, else fallback)
    this._setRating(data && data.rating != null ? data.rating : this.cfg.fallbackRating, data && data.ratingCount != null ? data.ratingCount : this.cfg.fallbackRatingCount);

    // shared add-to-cart context (product url + variant media)
    if (this.refs.form && data && data.url) this.refs.form.dataset.productUrl = data.url;
    if (this.refs.addContainer && data) {
      // cartImage = whatever led the gallery before a video poster could take slot 0
      const cartImg = data.cartImage || (data.images && data.images[0]);
      if (cartImg) this.refs.addContainer.dataset.productVariantMedia = cartImg;
    }

    this._customActive = isCustom;
    if (isCustom) {
      // custom bundle: the colour picker replaces "what's inside"; add-to-cart is gated by the builder
      const inside = this.querySelector('.bundle-builder__inside');
      if (inside) inside.hidden = true;
      this._enterCustom(data);
      this._renderUpsells([]); // no per-bundle upsells in custom mode
    } else {
      if (this.refs.custom) this.refs.custom.hidden = true;
      this._clearCustomProps();
      this._setSwatches(data && data.whatsInside ? data.whatsInside : []);
      if (this.refs.variantId) this.refs.variantId.value = available ? data.variantId : '';
      this._available = available;
      this._renderUpsells(this._upsellsFor(data)); // _syncAddState runs at its end
    }
  }

  // ---------- custom bundle (build-your-own colour picker) ----------
  _pad2(n) {
    return (n < 10 ? '0' : '') + n;
  }

  _rowsCount() {
    return this.refs.rows ? this.refs.rows.querySelectorAll('.bundle-builder__group').length : 0;
  }

  _enterCustom(data) {
    const r = this.refs;
    if (r.custom) r.custom.hidden = false;

    // (re)initialise state only when the bundle identity changes — keeps picks across row switches
    const cfg = data.custom || {};
    const colors = cfg.colors || [];
    const sig = (data.variantId || '') + ':' + (cfg.count || 0) + ':' + colors.map((c) => c.key).join(',');
    if (!this.customState || this.customState.sig !== sig) {
      // seed picks restored from the shareable URL through this same init: keep only known
      // (and available) colour keys, preserve order, truncate to the slot count
      let picks = [];
      if (this._pendingPicks) {
        picks = this._pendingPicks
          .filter((k) => colors.some((c) => c.key === k && c.available))
          .slice(0, cfg.count || 0);
        this._pendingPicks = null;
      }
      this.customState = {
        sig,
        count: cfg.count || 0,
        colors,
        picks,
        activeTab: '',
        label: (this.leaf && this.leaf.label) || 'הרכבה אישית',
        variantId: data.variantId,
      };
    }

    if (r.customNum) r.customNum.textContent = this._pad2(this._rowsCount() + 1);
    if (r.customTitle) r.customTitle.textContent = cfg.colorsHeading || 'צבעים';
    if (r.customSelLabel) r.customSelLabel.textContent = this.cfg.customSelectionLabel || 'הבחירה שלך';

    this._renderCustomTabs();
    this._renderCustomColors();
    this._renderCircles();
    this._syncCustom();
  }

  _renderCustomTabs() {
    const el = this.refs.customTabs;
    if (!el) return;
    const st = this.customState;
    const tabVals = [];
    st.colors.forEach((c) => {
      if (c.tab && tabVals.indexOf(c.tab) === -1) tabVals.push(c.tab);
    });
    el.innerHTML = '';
    if (!tabVals.length) {
      el.hidden = true;
      return;
    }
    el.hidden = false;
    const tabs = [{ val: '', label: this.cfg.customAllTab || 'הכל' }].concat(
      tabVals.map((t) => ({ val: t, label: t }))
    );
    tabs.forEach((t) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'bundle-builder__tab' + (t.val === st.activeTab ? ' is-active' : '');
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-selected', t.val === st.activeTab ? 'true' : 'false');
      b.textContent = t.label;
      b.addEventListener('click', () => {
        st.activeTab = t.val;
        this._renderCustomTabs();
        this._renderCustomColors();
      });
      el.appendChild(b);
    });
  }

  _applySwatch(el, color) {
    if (!color) return;
    if (color.swatch) {
      // explicit uploaded swatch image
      el.style.setProperty('--sw', 'url("' + color.swatch + '")');
      el.style.removeProperty('--swc');
    } else if (color.color) {
      // explicit hex colour — a flat swatch (wins over the product photo)
      el.style.setProperty('--swc', color.color);
      el.style.setProperty('--sw', 'none');
    } else if (color.variantImage) {
      // last resort: the matched variant's own image
      el.style.setProperty('--sw', 'url("' + color.variantImage + '")');
      el.style.removeProperty('--swc');
    }
  }

  _countOf(key) {
    return this.customState.picks.filter((k) => k === key).length;
  }

  _stepBtn(act, label, disabled) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'bundle-builder__step';
    b.setAttribute('data-act', act);
    b.setAttribute('aria-label', act === 'inc' ? 'הוספה' : 'הפחתה');
    b.textContent = label;
    b.disabled = !!disabled;
    return b;
  }

  _renderCustomColors() {
    const ul = this.refs.customColors;
    if (!ul) return;
    const st = this.customState;
    const total = st.picks.length;
    ul.innerHTML = '';

    st.colors.forEach((color) => {
      const li = document.createElement('li');
      li.className = 'bundle-builder__color';
      li.dataset.colorKey = color.key;
      li.hidden = !!(st.activeTab && color.tab !== st.activeTab);

      const sw = document.createElement('span');
      sw.className = 'bundle-builder__color-swatch';
      this._applySwatch(sw, color);
      li.appendChild(sw);

      const name = document.createElement('span');
      name.className = 'bundle-builder__color-name';
      name.textContent = color.label || '';
      li.appendChild(name);

      const count = this._countOf(color.key);
      const stepper = document.createElement('div');
      stepper.className = 'bundle-builder__stepper';
      const dec = this._stepBtn('dec', '−', count === 0 || !color.available);
      const cnt = document.createElement('span');
      cnt.className = 'bundle-builder__step-count';
      cnt.textContent = count;
      const inc = this._stepBtn('inc', '+', total >= st.count || !color.available);
      dec.addEventListener('click', () => this._customDec(color.key));
      inc.addEventListener('click', () => this._customInc(color.key));
      stepper.appendChild(dec);
      stepper.appendChild(cnt);
      stepper.appendChild(inc);
      li.appendChild(stepper);

      ul.appendChild(li);
    });

    if (this.refs.customHint) {
      const overflow = ul.scrollHeight > ul.clientHeight + 2;
      this.refs.customHint.hidden = !overflow;
      this.refs.customHint.textContent = this.cfg.customScrollHint || 'גללי לעוד צבעים';
    }
  }

  _refreshStepperStates() {
    const st = this.customState;
    const total = st.picks.length;
    if (!this.refs.customColors) return;
    this.refs.customColors.querySelectorAll('.bundle-builder__color').forEach((li) => {
      const key = li.dataset.colorKey;
      const color = st.colors.find((c) => c.key === key);
      const count = this._countOf(key);
      const cnt = li.querySelector('.bundle-builder__step-count');
      if (cnt) cnt.textContent = count;
      const dec = li.querySelector('[data-act="dec"]');
      const inc = li.querySelector('[data-act="inc"]');
      if (dec) dec.disabled = count === 0 || !(color && color.available);
      if (inc) inc.disabled = total >= st.count || !(color && color.available);
    });
  }

  _customInc(key) {
    const st = this.customState;
    if (st.picks.length >= st.count) return;
    st.picks.push(key);
    this._afterPickChange();
  }

  _customDec(key) {
    const st = this.customState;
    const idx = st.picks.lastIndexOf(key);
    if (idx === -1) return;
    st.picks.splice(idx, 1);
    this._afterPickChange();
  }

  // update counts / circles / gating in place (no full re-render, so scroll position is kept)
  _afterPickChange() {
    this._refreshStepperStates();
    this._renderCircles();
    this._syncCustom();
    this._syncUrl(); // picks changed by the user — reflect them in the shareable URL
  }

  _renderCircles() {
    const ul = this.refs.customCircles;
    if (!ul) return;
    const st = this.customState;
    ul.innerHTML = '';
    for (let i = 0; i < st.count; i++) {
      const li = document.createElement('li');
      li.className = 'bundle-builder__selection-circle';
      const key = st.picks[i];
      if (key != null) {
        const color = st.colors.find((c) => c.key === key);
        li.classList.add('is-filled');
        if (color) {
          this._applySwatch(li, color);
          if (color.label) li.title = color.label;
        }
      } else {
        li.classList.add('is-empty');
      }
      ul.appendChild(li);
    }
  }

  _syncCustom() {
    const st = this.customState;
    const total = st.picks.length;
    const complete = st.count > 0 && total === st.count;

    if (this.refs.customMsg) {
      if (complete) {
        this.refs.customMsg.textContent = this.cfg.customReadyText || 'החבילה מלאה';
        this.refs.customMsg.classList.add('is-ready');
      } else {
        const remaining = st.count - total;
        const word =
          remaining === 1
            ? this.cfg.customOneColor || 'צבע אחד'
            : remaining + ' ' + (this.cfg.customColorsWord || 'צבעים');
        this.refs.customMsg.textContent = (this.cfg.customMorePrefix || 'בחרי עוד') + ' ' + word;
        this.refs.customMsg.classList.remove('is-ready');
      }
    }

    // custom bundle adds the real colour variants itself (multi-line) via a plain button — not the form
    if (this.refs.variantId) this.refs.variantId.value = '';
    if (this.refs.addBtn) {
      this.refs.addBtn.disabled = !complete;
    }
    if (this.refs.addText) this.refs.addText.textContent = this.cfg.addText || 'הוספה לעגלה';
    this._syncAddState(); // owns type + sticky mirror
  }

  // custom bundles bypass the theme's single-item form and add all chosen colour variants at once
  // ⚠️ ONE listener owns this button. Two preventDefaulting listeners on the store's only purchase
  // control is how double-adds happen.
  _bindCustomAdd() {
    const btn = this.refs.addBtn;
    if (!btn) return;
    btn.addEventListener('click', (e) => {
      // A normal bundle with NO pending extras is left entirely alone: the theme's own form submit
      // runs and we keep its fly animation, drawer, error UI and section morph untouched.
      const extras = this._customActive ? [] : this._pendingUpsellItems();
      if (!this._customActive && !extras.length) return;
      // fly-to-cart still happens: product-form.js binds handleClick in the CAPTURE phase, so it has
      // already run by the time this bubble-phase handler cancels the submit
      e.preventDefault();
      if (btn.disabled || this._customAdding) return;
      const items = this._customActive ? this._customItems() : this._bundleItem();
      if (!items) return;
      this._batchAdd(items.concat(extras), extras.length);
    });
  }

  // the bundle itself as a cart line — null when it is sold out (extras-only path)
  _bundleItem() {
    if (this._available === false) return []; // sold out: the extras go alone
    const id = this._leafData && this._leafData.variantId;
    return id ? [{ id: Number(id), quantity: 1 }] : [];
  }

  _customItems() {
    const st = this.customState;
    if (!st || st.count <= 0 || st.picks.length !== st.count) return null;
    // aggregate the picks into one line per colour; a shared id tags them as one bundle
    const byKey = {};
    st.picks.forEach((k) => {
      byKey[k] = (byKey[k] || 0) + 1;
    });
    const bundleId = 'bb-' + this._uid + '-' + Date.now();
    const items = [];
    st.colors.forEach((c) => {
      const q = byKey[c.key];
      if (q > 0 && c.variantId) {
        items.push({
          id: Number(c.variantId),
          quantity: q,
          properties: { _bundle_id: bundleId, _bundle: st.label },
        });
      }
    });
    return items.length ? items : null;
  }

  // ONE atomic request for the bundle (or the custom colour lines) plus every pending upsell.
  // /cart/add.js with items[] is all-or-nothing — probe-verified: one bad line returns 422 and the
  // cart is byte-unchanged — so the shopper can never end up half-added.
  async _batchAdd(items, extraCount) {
    if (!items || !items.length) return;

    // ask the cart-add response to render the drawer sections too, so we can morph them in
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean)
      .join(',');

    const btn = this.refs.addBtn;
    this._customAdding = true;
    // .disabled, not just a flag: the sticky bar proxies a click and reads the real button
    if (btn) btn.disabled = true;

    const addUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
    try {
      const res = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items, sections: sectionIds }),
      });
      const data = await res.json().catch(() => null); // 429/challenge pages are not JSON
      if (!res.ok || (data && data.status)) {
        console.warn('[bundle-builder] add failed:', data && data.description);
        this._flashAddError(); // the customer must SEE that nothing was added
        return; // nothing was added — the pending steppers stay put so retry is one tap
      }
      let cart = null;
      try {
        cart = await (await fetch('/cart.js')).json();
      } catch (e) {}
      // notify the theme's cart pipeline (bubbles to document → drawer opens + morphs its sections)
      try {
        const { CartAddEvent } = await import('@theme/events');
        const itemCount = items.reduce((n, i) => n + i.quantity, 0);
        const target = this.refs.form || this;
        target.dispatchEvent(
          new CartAddEvent(cart || undefined, this.sectionId, {
            source: 'product-form-component',
            itemCount: itemCount,
            sections: data && data.sections, // ⚠️ data can be null on a non-JSON 200
          })
        );
      } catch (e) {
        console.warn('[bundle-builder] cart event dispatch failed:', e);
      }
      // the extras are in the cart now — clear the pending selection so they cannot be added twice
      if (extraCount) {
        this._upQty.clear();
        this._renderUpsells(this._lastUpsells || []);
      }
    } catch (e) {
      console.error('[bundle-builder] add error:', e);
      this._flashAddError();
    } finally {
      // ⚠️ must run on EVERY exit path, including the early `return` above — otherwise a failed add
      // leaves the store's only purchase button disabled forever
      this._customAdding = false;
      this._syncAddState();
    }
  }

  // brief inline failure feedback on the ATC label — a silent failed add reads as a dead button
  _flashAddError() {
    const label = this.querySelector('[data-bb-add-text]');
    if (!label) return;
    if (this._addErrT) { clearTimeout(this._addErrT); }
    if (this._addErrOriginal == null) this._addErrOriginal = label.textContent;
    label.textContent = this.cfg.addErrorText || 'משהו השתבש — נסי שוב';
    this._addErrT = setTimeout(() => {
      label.textContent = this._addErrOriginal;
      this._addErrOriginal = null;
      this._addErrT = null;
    }, 2500);
  }

  // Sticky buy bar: revealed only after the REAL add-to-cart scrolls above the viewport
  // (scrolling down past it); the button proxies a click to [data-bb-add], so the entire
  // existing pipeline (theme form / custom add / fly animation / drawer-open) runs unchanged.
  disconnectedCallback() {
    this._unbindSticky(); // window listeners re-bind per connect (editor moves the node)
    this._unbindVideoWatchers();
    this._unloadVideo(); // a detached <video> with a src keeps buffering (and can keep playing)
  }

  _unbindSticky() {
    if (this._stickyOnScroll) {
      window.removeEventListener('scroll', this._stickyOnScroll);
      window.removeEventListener('resize', this._stickyOnScroll);
      this._stickyOnScroll = null;
    }
    this._stickyBound = false;
  }

  // rAF-throttled window scroll watcher (the comparison-table pattern), NOT an
  // IntersectionObserver: an instant jump (anchor link, restored scroll position,
  // programmatic scrollTo) teleports the ATC across the viewport without it ever
  // intersecting, so an IO never fires and the bar sticks in the wrong state.
  _bindSticky() {
    const bar = this.refs ? this.refs.sticky : null;
    if (!bar || this._stickyBound) return;
    this._stickyBound = true;
    if (this.refs.stickyBtn && !this._stickyClickBound) {
      this._stickyClickBound = true; // element-lifetime listener — never rebound on re-arms
      this.refs.stickyBtn.addEventListener('click', () => {
        if (this.refs.addBtn && !this.refs.addBtn.disabled) this.refs.addBtn.click();
      });
    }
    const anchor = this.refs.addContainer || this.refs.addBtn;
    if (!anchor) return;
    let ticking = false;
    this._stickyOnScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        if (!this._stickyOnScroll) return; // unbound mid-frame
        // show only when the ATC fully left through the TOP (scrolled down past it);
        // a display:none anchor reports a 0-rect → bottom 0 → stays hidden (safe)
        bar.hidden = !(anchor.getBoundingClientRect().bottom < 0);
        this._syncStickyState();
      });
    };
    window.addEventListener('scroll', this._stickyOnScroll, { passive: true });
    window.addEventListener('resize', this._stickyOnScroll, { passive: true });
    this._stickyOnScroll();
  }

  _syncStickyState() {
    if (this.refs.stickyBtn && this.refs.addBtn) {
      this.refs.stickyBtn.disabled = this.refs.addBtn.disabled;
    }
  }

  _clearCustomProps() {
    if (this.refs.props) this.refs.props.innerHTML = '';
  }

  // Open the theme's cart drawer after every successful add (normal bundles via the theme form,
  // custom bundles via _customAdd, upsells via the utility form). All of those dispatch a bubbling
  // CartAddEvent ('cart:update') from INSIDE this element, so an element-level listener only ever
  // sees our own adds — drawer-side quantity edits never reach it. The optional delay lets the
  // theme's fly-to-cart animation land on the cart icon before the drawer slides over it.
  _bindCartDrawerOpen() {
    if (this._drawerBound) return; // element listener: bind once per element (editor moves the node)
    this._drawerBound = true;
    this.addEventListener('cart:update', (e) => {
      const d = e && e.detail && e.detail.data;
      if (this.cfg && this.cfg.openDrawer === false) return;
      if (d && d.didError) return; // failed adds must not open the drawer
      const drawer = document.querySelector('cart-drawer-component');
      if (!drawer || typeof drawer.open !== 'function') return; // cart type "page" → no drawer
      const delay = Math.max(0, +((this.cfg || {}).openDrawerDelay) || 0);
      clearTimeout(this._drawerT);
      this._drawerT = setTimeout(() => {
        try { drawer.open(); } catch (err) {}
      }, delay);
    });
  }

  _setRating(rating, count) {
    const r = parseFloat(rating);
    const hasCount = count != null && count !== '';
    // remove the whole rating row (no leftover space / divider) when there's nothing to show
    if (this.refs.rating) this.refs.rating.hidden = isNaN(r) && !hasCount;
    if (this.refs.stars) {
      if (isNaN(r)) {
        this.refs.stars.innerHTML = '';
      } else {
        const rounded = Math.round(r);
        let html = '';
        for (let i = 1; i <= 5; i++) html += STAR.replace('{empty}', i > rounded ? ' is-empty' : '');
        this.refs.stars.innerHTML = html;
      }
    }
    if (this.refs.ratingValue) this.refs.ratingValue.textContent = isNaN(r) ? '' : r + ' / 5';
    if (this.refs.ratingCount) {
      const label = this.cfg.reviewsLabel || 'ביקורות';
      this.refs.ratingCount.textContent = hasCount ? count + ' ' + label : '';
    }
  }

  _setSwatches(list) {
    const ul = this.refs.swatches;
    if (!ul) return;
    ul.innerHTML = '';
    list.forEach((sw) => {
      const li = document.createElement('li');
      li.className = 'bundle-builder__swatch';
      if (sw && sw.src) li.style.setProperty('--bb-swatch', 'url("' + sw.src + '")');
      if (sw && sw.label) li.title = sw.label;
      ul.appendChild(li);
    });
    const wrap = ul.closest('.bundle-builder__inside');
    if (wrap) wrap.hidden = list.length === 0;
  }

  // ---------- upsells (resolved per bundle from the section pool) ----------
  _renderUpsells(list) {
    const wrap = this.refs.upsell;
    const ul = this.refs.upsellList;
    if (!wrap || !ul) return;
    const items = (list || []).filter((u) => u && u.variantId);
    // ⚠️ THE SAFETY RULE: drop any pending quantity whose upsell is not on the leaf we are about to
    // render. A shopper must never be charged for something they can no longer see — this single
    // line is also what makes entering custom mode (which renders []) clear everything.
    const keep = new Set(items.map((u) => String(u.key)));
    Array.from(this._upQty.keys()).forEach((k) => {
      if (!keep.has(k)) this._upQty.delete(k);
    });
    this._lastUpsells = items;
    ul.innerHTML = '';
    // a rendered 0-height element still eats a flex-gap slot in the info column, so the wrapper
    // itself must go away when there is nothing to show
    wrap.hidden = items.length === 0;
    if (this.cfg.upsellDivider !== false) wrap.classList.add('bundle-builder__upsell--divided');
    const addText = this.cfg.upsellAddText || 'הוספה';
    const soldOut = this.cfg.upsellSoldOutText || 'אזל';

    items.forEach((u) => {
      const row = document.createElement('div');
      row.className = 'bundle-builder__upsell-item';

      // a badge element, styled by section vars + optional per-upsell inline colours (product-card
      // precedent — colours precomposed server-side, never a raw value in JS)
      const badgeEl = (extraClass) => {
        if (!u.badge) return null;
        const b = document.createElement('span');
        b.className = 'bundle-builder__upsell-badge' + (extraClass ? ' ' + extraClass : '');
        if (this.cfg.upsellBadgeRing !== false) b.classList.add('bundle-builder__upsell-badge--ring');
        b.textContent = u.badge;
        if (u.badgeStyle) b.setAttribute('style', u.badgeStyle);
        return b;
      };
      const badgePos = this.cfg.upsellBadgePosition || 'corner';

      // the thumb is only a BUTTON when there is media behind it — a control that opens nothing
      // is worse than no control. It is wrapped so a corner badge can overhang without being
      // clipped by the thumb's overflow:hidden (which the image rounding needs).
      const hasMedia = Array.isArray(u.media) && u.media.length > 0;
      const wrap = document.createElement('div');
      wrap.className = 'bundle-builder__upsell-thumb-wrap';
      const thumb = document.createElement(hasMedia ? 'button' : 'div');
      thumb.className = 'bundle-builder__upsell-thumb';
      if (hasMedia) {
        thumb.type = 'button';
        thumb.setAttribute('aria-haspopup', 'dialog');
        thumb.setAttribute('aria-label', (this.cfg.upsellGalleryLabel || 'גלריית המוצר') + ': ' + (u.title || ''));
      }
      if (u.image) {
        const img = document.createElement('img');
        img.src = u.image;
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        thumb.appendChild(img);
      }
      if (hasMedia) {
        const z = document.createElement('span');
        z.className = 'bundle-builder__upsell-zoom';
        z.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M4 9V4h5M20 15v5h-5M15 4h5v5M9 20H4v-5"/></svg>';
        thumb.appendChild(z);
        // warm the module on intent, run it on the click — first open then costs no round-trip
        const warm = () => this._warmLightbox();
        thumb.addEventListener('pointerenter', warm, { once: true });
        thumb.addEventListener('touchstart', warm, { once: true, passive: true });
        thumb.addEventListener('click', () => this._openUpsellMedia(u, thumb));
      }
      wrap.appendChild(thumb);
      // corner badge sits on the thumbnail (can overhang the edge); name badge goes in the text column
      if (badgePos === 'corner') {
        const b = badgeEl('bundle-builder__upsell-badge--corner-' + (this.cfg.upsellBadgeCorner || 'ts'));
        if (b) wrap.appendChild(b);
      }
      row.appendChild(wrap);

      const info = document.createElement('div');
      info.className = 'bundle-builder__upsell-info';
      if (badgePos === 'name') {
        const b = badgeEl('bundle-builder__upsell-badge--name');
        if (b) info.appendChild(b);
      }
      const title = document.createElement('span');
      title.className = 'bundle-builder__upsell-title';
      title.textContent = u.title || '';
      info.appendChild(title);
      if (u.subtitle) {
        const sub = document.createElement('span');
        sub.className = 'bundle-builder__upsell-sub';
        sub.textContent = u.subtitle;
        info.appendChild(sub);
      }
      const price = document.createElement('span');
      price.className = 'bundle-builder__upsell-price';
      // precomposed server-side (money is NEVER formatted in JS) — this is the saveChip precedent
      price.innerHTML = (u.priceHtml || '') + (u.chipHtml || '');
      info.appendChild(price);
      row.appendChild(info);

      // fixed-width slot holding EITHER the add button or the stepper
      const slot = document.createElement('div');
      slot.className = 'bundle-builder__upsell-ctl';
      slot.setAttribute('data-bb-up-ctl', '');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'button button-secondary bundle-builder__upsell-add';
      btn.textContent = u.available ? u.buttonText || addText : u.soldOutText || soldOut;
      // name the product: three identical "הוספה" buttons are meaningless to a screen reader
      btn.setAttribute('aria-label', (u.buttonText || addText) + ' — ' + (u.title || ''));
      if (!u.available) btn.disabled = true;
      // ⚠️ this no longer touches the cart: it opens the stepper, and the whole selection is
      // committed by the MAIN add-to-cart button in one atomic request
      btn.addEventListener('click', () => this._setUpQty(u, 1, row));
      slot.appendChild(btn);
      row.appendChild(slot);
      row.dataset.upKey = String(u.key);

      ul.appendChild(row);
      this._paintUpsellCtl(row, u); // restores a quantity that survived an option change
    });
    this._syncAddState();
  }

  // The SINGLE owner of the add button's type/disabled/label. `type` is the structural gate: on the
  // batch path it is 'button', so the native form submit cannot fire at all and a missed
  // preventDefault can never race the theme into a double add.
  _syncAddState() {
    const r = this.refs;
    if (!r.addBtn) return;
    const custom = !!(this.customState && this.leaf && this.leaf.custom);
    const pending = this._pendingUpsellCount();
    const available = this._available !== false;
    // extras stay buyable when the bundle itself is sold out (owner's call) — the button changes
    // meaning rather than dying, which is what the old per-row buttons used to allow
    const extrasOnly = !custom && !available && pending > 0 && this.cfg.upsellAllowSoldOut !== false;

    if (custom) {
      // the custom path owns its own completeness rule
      r.addBtn.type = 'button';
    } else {
      r.addBtn.type = pending > 0 || extrasOnly ? 'button' : 'submit';
      r.addBtn.disabled = !(available || extrasOnly);
      if (r.addText) {
        r.addText.textContent = extrasOnly
          ? this.cfg.upsellOnlyText || 'הוספת התוספות לסל'
          : available
            ? this.cfg.addText || 'הוספה לעגלה'
            : this.cfg.soldOutText || 'אזל מהמלאי';
      }
      if (r.variantId) r.variantId.value = available ? (this._leafData && this._leafData.variantId) || '' : '';
    }
    this._syncStickyState();
    this._syncUpsellNote(pending);
  }

  _syncUpsellNote(pending) {
    const note = this.querySelector('[data-bb-upsell-note]');
    if (!note) return;
    note.hidden = !pending;
    if (pending) {
      const t = this.cfg.upsellNoteText || 'התוספות יתווספו יחד עם המארז';
      note.textContent = t;
    }
  }

  // ---------- pending upsell quantities ----------
  _upMax(u) {
    const cap = +this.cfg.upsellQtyMax || 10;
    const stock = u && u.maxQty != null ? +u.maxQty : Infinity;
    return Math.max(1, Math.min(cap, stock));
  }

  _upQtyOf(key) {
    return this._upQty.get(String(key)) || 0;
  }

  _setUpQty(u, qty, row) {
    const key = String(u.key);
    const max = this._upMax(u);
    const q = Math.max(0, Math.min(qty, max));
    if (q <= 0) this._upQty.delete(key);
    else this._upQty.set(key, q);
    if (row) this._paintUpsellCtl(row, u);
    this._syncAddState();
    this._announceUpsell(u, q);
  }

  _pendingUpsellItems() {
    const out = [];
    (this._lastUpsells || []).forEach((u) => {
      const q = this._upQtyOf(u.key);
      if (q > 0 && u.variantId) out.push({ id: Number(u.variantId), quantity: q });
    });
    return out;
  }

  _pendingUpsellCount() {
    return this._pendingUpsellItems().reduce((n, i) => n + i.quantity, 0);
  }

  _announceUpsell(u, q) {
    const live = this.querySelector('[data-bb-upsell-live]');
    if (!live) return;
    live.textContent = q > 0 ? (u.title || '') + ' — ' + (this.cfg.upsellQtyLabel || 'כמות') + ' ' + q : '';
  }

  // swap the row's control between the "add" button and a stepper. The slot is fixed-width in CSS,
  // so this never re-wraps the product title.
  _paintUpsellCtl(row, u) {
    const slot = row.querySelector('[data-bb-up-ctl]');
    if (!slot) return;
    const qty = this._upQtyOf(u.key);
    const addBtn = slot.querySelector('.bundle-builder__upsell-add');
    let stepper = slot.querySelector('.bundle-builder__stepper');

    if (qty <= 0) {
      if (stepper) stepper.remove();
      if (addBtn) addBtn.hidden = false;
      return;
    }
    if (addBtn) addBtn.hidden = true;
    const max = this._upMax(u);
    if (!stepper) {
      stepper = document.createElement('div');
      stepper.className = 'bundle-builder__stepper bundle-builder__stepper--sm';
      stepper.setAttribute('role', 'group');
      stepper.setAttribute('aria-label', (this.cfg.upsellQtyLabel || 'כמות') + ' — ' + (u.title || ''));
      // DOM order dec -> count -> inc: in RTL that puts − on the right and + on the left with no
      // dir override, matching the custom-bundle stepper
      const dec = this._stepBtn('dec', '−', false);
      const count = document.createElement('span');
      count.className = 'bundle-builder__step-count';
      const inc = this._stepBtn('inc', '+', false);
      dec.addEventListener('click', () => this._setUpQty(u, this._upQtyOf(u.key) - 1, row));
      inc.addEventListener('click', () => this._setUpQty(u, this._upQtyOf(u.key) + 1, row));
      stepper.appendChild(dec);
      stepper.appendChild(count);
      stepper.appendChild(inc);
      slot.appendChild(stepper);
    }
    const cnt = stepper.querySelector('.bundle-builder__step-count');
    if (cnt) cnt.textContent = qty; // a bare integer — no <bdi>, it is not an LTR compound
    const dec = stepper.querySelector('[data-act="dec"]');
    const inc = stepper.querySelector('[data-act="inc"]');
    if (dec) dec.disabled = false; // reaching 0 is how the shopper removes it
    if (inc) inc.disabled = qty >= max;
  }

  // the lightbox is a separate module fetched on demand: a visitor who never opens a gallery pays
  // zero bytes for it
  _warmLightbox() {
    if (this._ulbPromise) return this._ulbPromise;
    const dlg = this.querySelector('[data-bb-ulb]');
    const src = dlg && dlg.getAttribute('data-bb-ulb-src');
    if (!src) return null;
    this._ulbPromise = import(src)
      .then((mod) => {
        const Ctor = mod && (mod.UpsellMediaLightbox || mod.default);
        if (!Ctor) return null;
        if (!this._ulb) {
          this._ulb = new Ctor(dlg);
          this._ulb.autoplay = this.cfg.upsellAutoplay !== false;
          this._ulb.sound = this.cfg.upsellGallerySound !== false;
          this._ulb.soundLabel = this.cfg.upsellSoundLabel || 'הפעלת קול';
          this._ulb.indicator = this.cfg.upsellGalleryIndicator || 'dots';
        }
        return this._ulb;
      })
      .catch(() => {
        this._ulbPromise = null; // a failed fetch must not poison later attempts
        return null;
      });
    return this._ulbPromise;
  }

  _openUpsellMedia(u, opener) {
    const p = this._warmLightbox();
    if (!p) return;
    p.then((lb) => {
      if (lb) lb.open(u, opener);
    });
  }

  // route the add through the hidden utility <product-form-component> so the theme cart pipeline runs
  // ---------- gallery ----------
  _bindGallery() {
    if (this.refs.prev) this.refs.prev.addEventListener('click', () => this._step(-1));
    if (this.refs.next) this.refs.next.addEventListener('click', () => this._step(1));
    this._bindGallerySwipe();
  }

  _setGallery(images, video) {
    // a new leaf: drop the previous cover immediately (no grace — its video must never be seen
    // over the next leaf's image) and let the reducer decide about the new one
    this._unloadVideo();
    this.video = video || null;
    this._videoFailed = false;
    this._videoMuted = this._videoWantsSound() ? false : true;
    this.gallery.images = images;
    this.gallery.index = 0;
    this._renderThumbs();
    this._setMain(0);
  }

  _renderThumbs() {
    const ul = this.refs.thumbs;
    if (!ul) return;
    ul.innerHTML = '';
    const imgs = this.gallery.images;
    const showThumbs = imgs.length > 1;
    ul.hidden = !showThumbs;
    if (this.refs.prev) this.refs.prev.hidden = !showThumbs;
    if (this.refs.next) this.refs.next.hidden = !showThumbs;
    if (!showThumbs) return;

    imgs.forEach((src, i) => {
      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      const isVideoThumb = i === 0 && !!this.video && this.cfg.videoBadge !== false;
      btn.className =
        'bundle-builder__thumb' + (i === 0 ? ' is-active' : '') + (isVideoThumb ? ' bundle-builder__thumb--video' : '');
      btn.setAttribute('aria-label', isVideoThumb ? 'סרטון' : 'תמונה ' + (i + 1));
      const img = document.createElement('img');
      img.src = src;
      img.alt = '';
      img.loading = 'lazy';
      btn.appendChild(img);
      btn.addEventListener('click', () => this._setMain(i));
      li.appendChild(btn);
      ul.appendChild(li);
    });
  }

  // Decode-gated crossfade: the outgoing image stays on the stage until the incoming one has
  // decoded, then the new <img> is stacked over it (CSS grid), faded in (180ms) and the old
  // layer(s) removed — so switching bundles never flashes an empty/placeholder stage.
  _setMain(index) {
    const imgs = this.gallery.images;
    const main = this.refs.main;
    if (!main) return;
    const gen = (this._mainGen = (this._mainGen || 0) + 1); // rapid swaps: only the latest commits
    if (!imgs.length) {
      main.innerHTML = PLACEHOLDER_IMG;
      return;
    }
    const prevIndex = this.gallery.index;
    this.gallery.index = (index + imgs.length) % imgs.length;
    // the video cover lives at slot 0: re-evaluate HERE, not in commit() — _setMain has three
    // exits that never reach commit (empty gallery, the pre-hydrator no-op, the failure path)
    this._syncVideo();

    // thumbnail active-state sync is immediate — never gated on the incoming image
    const syncThumbs = () => {
      if (!this.refs.thumbs) return;
      Array.from(this.refs.thumbs.querySelectorAll('.bundle-builder__thumb')).forEach((t, i) =>
        t.classList.toggle('is-active', i === this.gallery.index)
      );
    };
    syncThumbs();

    // seamless takeover from the inline PRE-HYDRATOR (and same-image re-selects): if the stage
    // holds exactly ONE img showing this src, don't rebuild/crossfade. Only the single-img case
    // is safe — mid-crossfade the stage holds [old, new] and an early return here would race
    // the 220ms cleanup timer (it would remove the matched layer after we kept it). Compare the
    // raw attribute too (leaf JSON urls are protocol-relative; .src is absolutized).
    const nextSrc = imgs[this.gallery.index];
    const layers = main.querySelectorAll('img');
    if (
      layers.length === 1 &&
      (layers[0].getAttribute('src') === nextSrc || layers[0].src === nextSrc)
    ) {
      return;
    }

    const wasEmpty = !main.querySelector('img, svg'); // nothing showing yet = first paint
    const img = document.createElement('img');
    img.decoding = 'async';
    img.alt = '';
    if (wasEmpty) {
      try {
        img.fetchPriority = 'high';
      } catch (e) {}
    }
    img.src = imgs[this.gallery.index]; // already sized in Liquid (image_url: width:)

    let done = false;
    let capTimer = 0;
    const commit = (failed) => {
      if (done) return;
      done = true;
      clearTimeout(capTimer);
      if (gen !== this._mainGen) return; // a newer swap superseded this one
      if (failed) {
        // keep whatever is currently showing; an empty stage falls back to the placeholder
        if (!main.querySelector('img, svg')) {
          main.innerHTML = PLACEHOLDER_IMG;
        } else {
          // the old image stays on display — roll index + thumb highlight back to match it
          this.gallery.index = prevIndex;
          syncThumbs();
          this._syncVideo(); // the model index moved back; the video must follow it
        }
        return;
      }
      const old = Array.from(main.children);
      img.classList.add('bb-img-enter');
      main.appendChild(img);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => img.classList.remove('bb-img-enter')); // fade in (180ms CSS)
      });
      setTimeout(() => {
        // drop the outgoing layer(s); a later commit removes THIS img the same way
        old.forEach((el) => {
          if (el.parentNode === main) main.removeChild(el);
        });
      }, 220);
    };

    // slow-network cap: after 2.5s commit anyway and let the image render progressively
    capTimer = setTimeout(() => commit(false), 2500);

    const fallbackEvents = () => {
      if (img.complete) {
        commit(img.naturalWidth === 0);
        return;
      }
      img.onload = () => commit(false);
      img.onerror = () => commit(true);
    };
    if (typeof img.decode === 'function') {
      // decode() can reject for images that would still paint fine — fall back to load state/events
      img.decode().then(() => commit(false), fallbackEvents);
    } else {
      fallbackEvents();
    }
  }

  _step(dir) {
    if (this.gallery.images.length) this._setMain(this.gallery.index + dir);
  }

  // Swipe the MAIN image to the next/previous gallery image (owner request 2026-07-25).
  // Mirrors feature-carousel's pointer swipe: 40px horizontal threshold, must be clearly more
  // horizontal than vertical; touch-action pan-y keeps vertical page scrolling native (and kills
  // double-tap zoom on the stage). Reading-forward: swipe left = next in LTR, swipe right in RTL.
  // Rapid swipes are safe — _setMain is decode-gated with a generation token.
  // ---------- video cover (optional, per option) ----------
  // • The <video> is created by JS inside [data-bb-video], a SIBLING of the crossfade container:
  //   _setMain wipes [data-bb-main] with innerHTML, which would silently detach a playing video
  //   and leave it buffering (audible in some webviews). The poster image underneath — the
  //   leaf's images[0] — is the permanent, free fallback.
  // • ONE predicate decides everything, re-evaluated from every state change (leaf, gallery
  //   index, viewport, tab visibility, bfcache, connect/disconnect, media error).
  // • Nothing is fetched until that predicate is true: an off-screen bundle costs ZERO video bytes.
  // • Autoplay is never assumed. play() is promise-guarded, and a refusal (iOS Low-Power mode,
  //   gesture-gated Android WebViews) leaves the poster in place, shows a play button, and takes
  //   ONE opportunistic retry on the visitor's next tap — never a retry loop.
  _initVideo() {
    this.video = null; // the current leaf's video data, or null
    this._videoEl = null;
    this._videoPick = null;
    this._videoFailed = false;
    this._videoMuted = this._videoWantsSound() ? false : true;
    this._videoToken = 0;
    this._stageVisible = false;
    this._userWantsVideo = false;
    if (!this.refs.videoLayer) return;
    this._rmq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const conn = navigator.connection || {};
    this._saveData = !!conn.saveData;
    this._slowNet = /^(slow-)?2g$/.test(conn.effectiveType || '');
    // element listeners bind once per element (the editor moves the node); window/document
    // listeners are re-bound per connect in _bindVideoWatchers
    if (!this._videoBoundEl) {
      this._videoBoundEl = true;
      if (this.refs.videoPlay) {
        this.refs.videoPlay.addEventListener('click', () => this._playVideo(true));
      }
      if (this.refs.videoSound) {
        this.refs.videoSound.addEventListener('click', () => this._toggleVideoSound());
      }
    }
    this._bindVideoWatchers();
  }

  _bindVideoWatchers() {
    if (!this.refs || !this.refs.videoLayer || this._videoWatchBound) return;
    this._videoWatchBound = true;
    const stage = this.refs.main && this.refs.main.parentElement ? this.refs.main.parentElement : this;
    // IntersectionObserver is correct here: the predicate is a STATE ("is the gallery on screen"),
    // not a crossing — after an instant scroll jump the IO still reports the final state. (The
    // project's rAF-scroll-watcher rule exists for crossing predicates like the sticky bar.)
    this._videoIO = new IntersectionObserver(
      (entries) => {
        const e = entries[entries.length - 1];
        if (!e) return;
        this._stageVisible = e.isIntersecting;
        this._syncVideo();
      },
      { threshold: 0.25 }
    );
    this._videoIO.observe(stage);
    this._onVideoVis = this._onVideoVis || (() => this._syncVideo());
    document.addEventListener('visibilitychange', this._onVideoVis);
    this._onVideoShow = this._onVideoShow || ((e) => { if (e.persisted) this._syncVideo(); });
    window.addEventListener('pageshow', this._onVideoShow);
    // only one bundle cover on the page may have sound
    this._onVideoSound = this._onVideoSound || ((e) => {
      if (e.detail !== this && !this._videoMuted) this._setVideoMuted(true);
    });
    document.addEventListener('siclips:bb-video-sound', this._onVideoSound);
  }

  _unbindVideoWatchers() {
    if (!this._videoWatchBound) return;
    this._videoWatchBound = false;
    if (this._videoIO) { this._videoIO.disconnect(); this._videoIO = null; }
    if (this._onVideoVis) document.removeEventListener('visibilitychange', this._onVideoVis);
    if (this._onVideoShow) window.removeEventListener('pageshow', this._onVideoShow);
    if (this._onVideoSound) document.removeEventListener('siclips:bb-video-sound', this._onVideoSound);
    this._detachGestureRetry();
  }

  // the merchant switch + real data (does a cover exist at all for this leaf?)
  _videoEnabled() {
    return !!(this.refs && this.refs.videoLayer && this.video && !this._videoFailed && this.cfg.videoAutoplay !== false);
  }

  // browser/visitor policy: may it start WITHOUT a gesture?
  _videoAutoAllowed() {
    if (!this._videoEnabled()) return false;
    if (this._rmq && this._rmq.matches) return false;
    if (this._saveData || this._slowNet) return false;
    if (this.cfg.videoMobile === false && window.matchMedia('(max-width: 749px)').matches) return false;
    return true;
  }

  _videoWanted() {
    return this._videoEnabled() && this.gallery.index === 0 && this._stageVisible && !document.hidden;
  }

  _syncVideo() {
    if (!this.refs || !this.refs.videoLayer || this._videoQueued) return;
    this._videoQueued = true; // coalesce: several call sites fire within one tick
    Promise.resolve().then(() => {
      this._videoQueued = false;
      this._syncVideoNow();
    });
  }

  _syncVideoNow() {
    if (!this.refs || !this.refs.videoLayer) return;
    const onSlot = this._videoEnabled() && this.gallery.index === 0;
    if (!onSlot) {
      // no cover, or the customer moved to another image: stop now, unload after a short grace
      // so a 0 → 1 → 0 swipe costs nothing
      this._pauseVideo();
      if (this.refs.videoPlay) this.refs.videoPlay.hidden = true;
      if (this._videoEl && !this._videoGraceT) {
        this._videoGraceT = setTimeout(() => {
          this._videoGraceT = null;
          if (!this._videoEnabled() || this.gallery.index !== 0) this._unloadVideo();
        }, 1200);
      }
      return;
    }
    clearTimeout(this._videoGraceT);
    this._videoGraceT = null;
    if (!this._stageVisible || document.hidden) {
      this._pauseVideo(); // keeps the buffer: coming back resumes instantly
      return;
    }
    if (this._videoAutoAllowed() || this._userWantsVideo) {
      if (!this._videoEl || this._videoEl.paused) this._playVideo(false);
    } else if (this.refs.videoPlay) {
      this.refs.videoPlay.hidden = false; // policy says no autostart — still offer the choice
    }
  }

  // "Start with sound on" means exactly one thing: ATTEMPT an unmuted start. Browsers refuse an
  // unmuted autoplay until the visitor has interacted with the page, and when they refuse we
  // simply play muted and leave it at that — no waiting for a tap to sneak the sound on later
  // (owner, 2026-08-09: "if the browser doesn't allow that, don't do it"). The visitor can always
  // turn sound on with the speaker button, and the icon always shows the real state.
  _videoWantsSound() {
    return this.cfg && this.cfg.videoSoundDefault === true;
  }

  _pickVideoSource() {
    if (this._videoPick) return this._videoPick;
    const list = ((this.video && this.video.sources) || []).filter((x) => x && x.url);
    if (!list.length) return null;
    // mp4 ladder only — a short silent loop gains nothing from HLS and its fallback path is code
    // we would have to maintain for no benefit
    const mp4 = list
      .filter((x) => x.format !== 'm3u8' && !/mpegurl/i.test(x.mime || ''))
      .sort((a, b) => (a.height || 0) - (b.height || 0));
    const box = (this.refs.videoLayer && this.refs.videoLayer.offsetHeight) || 500;
    const need = box * Math.min(window.devicePixelRatio || 1, 2);
    this._videoPick = mp4.find((x) => (x.height || 0) >= need) || mp4[mp4.length - 1] || list[0];
    return this._videoPick;
  }

  _mountVideo() {
    if (this._videoEl || !this.refs.videoLayer) return null;
    const pick = this._pickVideoSource();
    if (!pick) return null;
    const token = ++this._videoToken;
    const v = document.createElement('video');
    v.muted = true; // property AND attribute: a later load() must still come back muted
    v.defaultMuted = true;
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', '');
    v.loop = true;
    v.preload = 'none';
    v.disablePictureInPicture = true;
    v.setAttribute('disableremoteplayback', '');
    v.setAttribute('aria-hidden', 'true');
    v.tabIndex = -1;
    if (this.video.poster) v.poster = this.video.poster;
    v.addEventListener('playing', () => {
      if (token !== this._videoToken) return; // a later leaf/source owns the stage now
      this.refs.videoLayer.classList.add('is-playing');
      if (this.refs.videoPlay) this.refs.videoPlay.hidden = true;
      this._detachGestureRetry();
      this._syncSoundBtn();
    });
    v.addEventListener('error', () => {
      if (token !== this._videoToken) return;
      this._videoFailed = true; // never retried for this leaf; the poster simply stays
      this._unloadVideo();
      this._syncVideo();
    });
    // src, never <source> children: removeAttribute('src') + load() is the only teardown that
    // reliably cancels the fetch and frees the decoder
    v.src = pick.url;
    this.refs.videoLayer.appendChild(v);
    this._videoEl = v;
    return v;
  }

  _playVideo(gesture) {
    if (!this._videoEnabled()) return;
    if (gesture) this._userWantsVideo = true;
    const v = this._videoEl || this._mountVideo();
    if (!v) return;
    v.muted = this._videoMuted;
    v.defaultMuted = v.muted;
    const p = v.play();
    if (p && p.catch) {
      p.catch(() => {
        if (!v.muted) {
          // an unmuted start was refused: play muted instead and make the icon tell the truth
          this._setVideoMuted(true);
          const p2 = v.play();
          if (p2 && p2.catch) p2.catch(() => this._videoNeedsGesture());
          return;
        }
        this._videoNeedsGesture();
      });
    }
  }

  _videoNeedsGesture() {
    if (!this.refs.videoLayer) return;
    // a source that FAILED gets no button: the play() rejection can land after the media error,
    // and a control that cannot do anything is worse than no control
    if (this._videoFailed) {
      if (this.refs.videoPlay) this.refs.videoPlay.hidden = true;
      this._detachGestureRetry();
      return;
    }
    this.refs.videoLayer.classList.remove('is-playing');
    if (this.refs.videoPlay) this.refs.videoPlay.hidden = false;
    this._syncSoundBtn();
    this._attachGestureRetry();
  }

  _attachGestureRetry() {
    if (this._gestureRetry) return;
    this._gestureRetry = () => {
      this._detachGestureRetry();
      if (this._videoWanted()) this._playVideo(true);
    };
    // Safari has historically wanted touch/click rather than pointerdown for transient activation
    document.addEventListener('pointerup', this._gestureRetry, { once: true, passive: true, capture: true });
    document.addEventListener('click', this._gestureRetry, { once: true, passive: true, capture: true });
  }

  _detachGestureRetry() {
    if (!this._gestureRetry) return;
    document.removeEventListener('pointerup', this._gestureRetry, true);
    document.removeEventListener('click', this._gestureRetry, true);
    this._gestureRetry = null;
  }

  _toggleVideoSound() {
    this._setVideoMuted(!this._videoMuted);
    if (!this._videoMuted) {
      document.dispatchEvent(new CustomEvent('siclips:bb-video-sound', { detail: this }));
    }
    // the press is a real gesture — use it to recover a refused autoplay too
    if (!this._videoEl || this._videoEl.paused) this._playVideo(true);
  }

  _setVideoMuted(m) {
    this._videoMuted = !!m;
    if (this._videoEl) {
      this._videoEl.muted = this._videoMuted;
      this._videoEl.defaultMuted = this._videoMuted;
    }
    this._syncSoundBtn();
  }

  _syncSoundBtn() {
    const btn = this.refs.videoSound;
    if (!btn) return;
    const playing = !!(this._videoEl && !this._videoEl.paused && this.refs.videoLayer && this.refs.videoLayer.classList.contains('is-playing'));
    // a speaker over a still poster would be a lying control — only offer it while it really plays
    btn.hidden = !(playing && this.cfg.videoSound !== false);
    btn.setAttribute('aria-pressed', this._videoMuted ? 'false' : 'true');
  }

  _pauseVideo() {
    if (this._videoEl && !this._videoEl.paused) {
      try { this._videoEl.pause(); } catch (e) {}
    }
    if (this.refs.videoLayer) this.refs.videoLayer.classList.remove('is-playing');
    this._syncSoundBtn();
  }

  _unloadVideo() {
    clearTimeout(this._videoGraceT);
    this._videoGraceT = null;
    this._videoToken++;
    this._videoPick = null;
    this._userWantsVideo = false;
    const v = this._videoEl;
    this._videoEl = null;
    if (v) {
      try { v.pause(); } catch (e) {}
      v.removeAttribute('src');
      try { v.load(); } catch (e) {} // this is the part that actually cancels the network
      if (v.parentNode) v.parentNode.removeChild(v);
    }
    if (this.refs && this.refs.videoLayer) this.refs.videoLayer.classList.remove('is-playing');
    if (this.refs && this.refs.videoPlay) this.refs.videoPlay.hidden = true;
    if (this.refs && this.refs.videoSound) this.refs.videoSound.hidden = true;
    this._detachGestureRetry();
  }

  _bindGallerySwipe() {
    if (this.cfg && this.cfg.gallerySwipe === false) return;
    const el = this.refs.main;
    if (!el) return;
    el.style.touchAction = 'pan-y';
    let x0 = null, y0 = null, pid = null;
    el.addEventListener('pointerdown', (e) => {
      if (e.button != null && e.button !== 0) return;
      x0 = e.clientX; y0 = e.clientY; pid = e.pointerId;
    });
    const end = (e) => {
      if (x0 == null || e.pointerId !== pid) return;
      const dx = e.clientX - x0;
      const dy = e.clientY - y0;
      x0 = null;
      if (this.gallery.images.length < 2) return;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.4) {
        const goNext = this.cfg && this.cfg.rtl ? dx > 0 : dx < 0;
        this._step(goNext ? 1 : -1);
      }
    };
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', () => { x0 = null; });
  }

  _renderEmpty() {
    if (this.refs.rows) {
      this.refs.rows.innerHTML = ''; // clear the Liquid-rendered skeleton too
      const p = document.createElement('p');
      p.className = 'bundle-builder__empty';
      p.textContent = this.cfg.emptyText || 'הוסיפי אפשרויות לבנאי החבילה מתוך עורך העיצוב.';
      this.refs.rows.appendChild(p);
    }
    this._setGallery([]);
    if (this.refs.addBtn) this.refs.addBtn.disabled = true;
    this._syncStickyState();
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
